import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel, Switch,
  FormControlLabel, CircularProgress, TextField, Alert, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon, TravelExplore as SbsIcon, Sync as SyncIcon } from '@mui/icons-material';
import { http } from '../../../../api/http';
import { AfectacionesService } from '../services/afectacionesService';
import type { AfectacionGeneral } from '../types/afectaciones';

export default function AfectacionesPage() {
  const [items, setItems] = useState<AfectacionGeneral[]>([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<AfectacionGeneral>({ idAfectacion: 0, idConceptoOrigen: 0, idConceptoDestino: 0, tipoAfectacion: 'DESCUENTO', activo: true });
  const [loading, setLoading] = useState(false);

  // SBS consultation state
  const [sbsDialog, setSbsDialog] = useState(false);
  const [sbsTasas, setSbsTasas] = useState<any[]>([]);
  const [sbsLoading, setSbsLoading] = useState(false);
  const [sbsUpdating, setSbsUpdating] = useState(false);
  const [sbsMessage, setSbsMessage] = useState<string | null>(null);

  useEffect(() => { load(); }, []);
  const load = async () => { try { setItems(await AfectacionesService.getAll()); } catch { /* handled */ } };

  const handleSave = async () => {
    setLoading(true);
    try { await AfectacionesService.save(form); setDialog(false); await load(); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar afectación?')) return;
    try { await AfectacionesService.delete(id); await load(); } catch { /* handled */ }
  };

  const handleConsultarSbs = async () => {
    setSbsDialog(true);
    setSbsLoading(true);
    setSbsMessage(null);
    try {
      const resp = await http.get('/planilla/sbs/tasas-afp');
      setSbsTasas(resp.data);
    } catch {
      setSbsMessage('Error al consultar tasas AFP de la SBS.');
    } finally {
      setSbsLoading(false);
    }
  };

  const handleActualizarTasas = async () => {
    setSbsUpdating(true);
    setSbsMessage(null);
    try {
      await http.post('/planilla/sbs/actualizar-tasas', sbsTasas);
      setSbsMessage('Tasas AFP actualizadas correctamente.');
    } catch {
      setSbsMessage('Error al actualizar tasas AFP.');
    } finally {
      setSbsUpdating(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'conceptoOrigen', headerName: 'Concepto Origen', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => row.conceptoOrigen ? `${row.conceptoOrigen.codigo} - ${row.conceptoOrigen.descripcion}` : '' },
    { field: 'conceptoDestino', headerName: 'Concepto Destino', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => row.conceptoDestino ? `${row.conceptoDestino.codigo} - ${row.conceptoDestino.descripcion}` : '' },
    { field: 'tipoAfectacion', headerName: 'Tipo', width: 120 },
    { field: 'activo', headerName: 'Activo', width: 80, type: 'boolean' },
    { field: 'acciones', headerName: '', width: 80, sortable: false,
      renderCell: (p) => <Button size="small" color="error" onClick={() => handleDelete(p.row.idAfectacion)}><DeleteIcon fontSize="small" /></Button> },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Afectaciones Generales</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="info" startIcon={<SbsIcon />} onClick={handleConsultarSbs}>
            Consultar SBS
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ idAfectacion: 0, idConceptoOrigen: 0, idConceptoDestino: 0, tipoAfectacion: 'DESCUENTO', activo: true }); setDialog(true); }}>Nueva Afectación</Button>
        </Stack>
      </Stack>
      <DataGrid rows={items} columns={columns} getRowId={(row) => row.idAfectacion} autoHeight density="compact" disableRowSelectionOnClick pageSizeOptions={[25]} />

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Afectación</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="ID Concepto Origen" type="number" fullWidth value={form.idConceptoOrigen || ''} onChange={(e) => setForm({ ...form, idConceptoOrigen: Number(e.target.value) })} />
            <TextField label="ID Concepto Destino" type="number" fullWidth value={form.idConceptoDestino || ''} onChange={(e) => setForm({ ...form, idConceptoDestino: Number(e.target.value) })} />
            <FormControl fullWidth>
              <InputLabel>Tipo Afectación</InputLabel>
              <Select value={form.tipoAfectacion} label="Tipo Afectación" onChange={(e) => setForm({ ...form, tipoAfectacion: e.target.value })}>
                <MenuItem value="DESCUENTO">Descuento</MenuItem>
                <MenuItem value="APORTE">Aporte</MenuItem>
                <MenuItem value="REGIMEN">Régimen</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />} label="Activo" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{loading ? <CircularProgress size={18} /> : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sbsDialog} onClose={() => setSbsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Tasas AFP - Consulta SBS</DialogTitle>
        <DialogContent>
          {sbsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sbsTasas.length > 0 ? (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>AFP</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Aporte Obligatorio (%)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Comision Flujo (%)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Comision Mixta (%)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Prima Seguro (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sbsTasas.map((t: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Chip label={t.nombreAfp || t.afp || 'N/A'} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{t.aporteObligatorio ?? '-'}</TableCell>
                    <TableCell align="right">{t.comisionFlujo ?? '-'}</TableCell>
                    <TableCell align="right">{t.comisionMixta ?? '-'}</TableCell>
                    <TableCell align="right">{t.primaSeguro ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Alert severity="warning" sx={{ mt: 1 }}>No se encontraron tasas AFP.</Alert>
          )}
          {sbsMessage && (
            <Alert severity={sbsMessage.startsWith('Error') ? 'error' : 'success'} sx={{ mt: 2 }}>
              {sbsMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSbsDialog(false)}>Cerrar</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={sbsUpdating ? <CircularProgress size={18} /> : <SyncIcon />}
            onClick={handleActualizarTasas}
            disabled={sbsUpdating || sbsTasas.length === 0}
          >
            Actualizar Tasas
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
